const SCALE_FACTOR = 0.9;
const BASE_MODAL_SCALE = 1 / SCALE_FACTOR; // ~1.1111

export const getViewportHeight = () => window.visualViewport?.height ?? window.innerHeight;

/**
 * Compute the visual scale for a modal.
 *
 * Mobile (portal — modals OUTSIDE wrapper):
 *   Topmost modal = scale 1.0, behind modals progressively smaller.
 *
 * Desktop (modals INSIDE wrapper):
 *   Each modal compensates for the wrapper's cumulative scale with (1/0.9)^(i+1).
 *
 * @param {number} index - Modal index in the stack
 * @param {number} stackSize - Total number of visible modals
 * @param {boolean} usePortal - Whether modals are rendered via portal (mobile)
 * @returns {number} Scale factor for this modal
 */
export const getModalScale = (index, stackSize, usePortal) => {
  if (usePortal) {
    // Outside wrapper: topmost = 1.0, behind = 0.9, 0.81, ...
    return Math.pow(SCALE_FACTOR, stackSize - 1 - index);
  }
  // Inside wrapper: compensate for wrapper's transform scale
  return Math.pow(BASE_MODAL_SCALE, index + 1);
};

/**
 * Compute all Y positions for the modal stack.
 *
 * Mobile (portal): positions are in VIEWPORT coordinates (position:fixed, Y=0 = viewport top).
 * Desktop: positions are in wrapper-local coordinates (position:absolute inside wrapper).
 *
 * @param {number} stackSize - Number of visible modals
 * @param {boolean} isDesktop - Whether we're on desktop
 * @param {number} wrapperHeight - The wrapper element's height
 * @param {number} wrapperScale - Current wrapper scale factor
 * @param {number[]} modalHeights - Array of measured modal heights
 * @returns {number[]} Array of Y positions for each modal
 */
/**
 * Desktop: cumulative staircase — modals inside wrapper.
 */
const _computeDesktopPositions = (stackSize) => {
  const positions = new Array(stackSize);
  for (let i = 0; i < stackSize; i++) {
    let cumY = 0;
    for (let j = 0; j <= i; j++) cumY += 60 - 15 * (stackSize - j);
    positions[i] = cumY;
  }
  return positions;
};

/**
 * Mobile: position behind modals relative to the one in front,
 * then clamp so the first modal doesn't go above viewport.
 */
const _computeMobilePositions = (stackSize, modalHeights, viewportH) => {
  const positions = new Array(stackSize);
  const topIndex = stackSize - 1;
  // istanbul ignore next: modalHeights[topIndex] is always truthy because _computeMobilePositions is only called when topH > 0
  const topH = modalHeights[topIndex] || 0;

  // Step 1: top modal — bottom at 16px from viewport bottom
  positions[topIndex] = viewportH - 16 - topH;

  // Step 2: work backwards — each behind modal peeks above the one in front
  for (let i = topIndex - 1; i >= 0; i--) {
    const currH = modalHeights[i] || 0;
    const nextH = modalHeights[i + 1] || 0;
    const currScale = getModalScale(i, stackSize, true);
    const nextScale = getModalScale(i + 1, stackSize, true);
    const staircaseGap = 60 - 15 * (stackSize - i);

    const nextVisualTop = positions[i + 1] + nextH * (1 - nextScale) / 2;
    const desiredVisualTop = nextVisualTop - staircaseGap;

    positions[i] = desiredVisualTop - currH * (1 - currScale) / 2;
  }

  // Step 3: clamp — ensure first modal stays within viewport
  _clampFirstModal(positions, stackSize, modalHeights);

  return positions;
};

/**
 * Shift all positions down if the first modal's visual top is above the viewport.
 */
const _clampFirstModal = (positions, stackSize, modalHeights) => {
  const firstH = modalHeights[0] || 0;
  if (firstH <= 0) return;

  const firstScale = getModalScale(0, stackSize, true);
  const firstVisualTop = positions[0] + firstH * (1 - firstScale) / 2;
  const minVisualTop = 20;

  if (firstVisualTop < minVisualTop) {
    const shift = minVisualTop - firstVisualTop;
    for (let i = 0; i < stackSize; i++) {
      positions[i] += shift;
    }
  }
};

export const computeAllPositions = (stackSize, isDesktop, wrapperHeight, wrapperScale, modalHeights) => {
  if (stackSize === 0) return [];

  if (isDesktop) {
    return _computeDesktopPositions(stackSize);
  }

  const viewportH = getViewportHeight();
  const topH = modalHeights[stackSize - 1] || 0;

  if (topH > 0) {
    return _computeMobilePositions(stackSize, modalHeights, viewportH);
  }

  // Fallback: heights not measured yet
  return new Array(stackSize).fill(viewportH - 300);
};
